import { useState, useMemo } from 'react';
import { Plus, X, Search, ChevronDown, ChevronRight, Check, Star, Pencil } from 'lucide-react';
import { useApi } from '../../lib/useApi';
import type { Account, Contact, MissionControlConfig, MissionControlObjective, MissionControlObjectiveContact } from '../../types';

interface Props {
  accounts: Account[];
  contacts: Contact[];
  config: MissionControlConfig | null;
  // Best outbound priority per contact_id, supplied by the AWS overview (the
  // server already collapses contact_use_case_scores to the winning priority).
  contactPriorities?: Record<string, string>;
  orgId: string;
  userId: string;
  leaderId: string | null;
  onCreated: () => void;
  onCancel: () => void;
  existingCycleId?: string;
  existingObjectives?: MissionControlObjective[];
  existingObjectiveContacts?: MissionControlObjectiveContact[];
}

interface SelectedAccount {
  account: Account;
  selectedContactIds: Set<string>;
}

const SENIORITY_RANK: Record<string, number> = {
  executive: 1,
  executive_leadership: 2,
  senior_leadership: 3,
  department_leader: 4,
  team_manager: 5,
  individual_contributor: 6,
};

const SENIORITY_LABELS: Record<string, string> = {
  executive: 'Exec',
  executive_leadership: 'Exec Lead',
  senior_leadership: 'Sr Lead',
  department_leader: 'Dept Lead',
  team_manager: 'Manager',
  individual_contributor: 'IC',
};

const TIER_ORDER: Record<string, number> = { A: 1, B: 2, C: 3 };

const PRIORITY_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  target_persona: { label: 'Target Persona', color: 'text-forest-700', bg: 'bg-forest-50' },
  decision_maker: { label: 'Decision Maker', color: 'text-app-focus', bg: 'bg-app-focus/10' },
  champion: { label: 'Champion', color: 'text-app-warning', bg: 'bg-app-warning/10' },
  economic_buyer: { label: 'Economic Buyer', color: 'text-app-focus', bg: 'bg-app-focus/10' },
  technical_buyer: { label: 'Technical Buyer', color: 'text-app-focus', bg: 'bg-app-focus/10' },
  influencer: { label: 'Influencer', color: 'text-app-muted', bg: 'bg-app-canvas' },
};

function sortContacts(
  contacts: Contact[],
  priorityMap: Map<string, string>,
): Contact[] {
  return [...contacts].sort((a, b) => {
    const aPriority = getPriorityRank(a, priorityMap);
    const bPriority = getPriorityRank(b, priorityMap);
    if (aPriority !== bPriority) return aPriority - bPriority;

    const aSeniority = SENIORITY_RANK[a.seniority] ?? 99;
    const bSeniority = SENIORITY_RANK[b.seniority] ?? 99;
    if (aSeniority !== bSeniority) return aSeniority - bSeniority;

    return (a.first_name || '').localeCompare(b.first_name || '');
  });
}

function getPriorityRank(contact: Contact, priorityMap: Map<string, string>): number {
  const ucPriority = priorityMap.get(contact.id);
  if (ucPriority === 'target_persona') return 1;
  if (ucPriority === 'decision_maker') return 2;
  if (contact.buying_committee_role === 'economic_buyer') return 3;
  if (contact.buying_committee_role === 'champion') return 3;
  if (contact.buying_committee_role === 'technical_buyer') return 4;
  if (ucPriority === 'influencer') return 5;
  if (contact.buying_committee_role === 'influencer') return 5;
  return 10;
}

function getContactBadge(
  contact: Contact,
  priorityMap: Map<string, string>,
): { label: string; color: string; bg: string } | null {
  const ucPriority = priorityMap.get(contact.id);
  if (ucPriority && PRIORITY_LABELS[ucPriority]) return PRIORITY_LABELS[ucPriority];
  if (contact.buying_committee_role && PRIORITY_LABELS[contact.buying_committee_role]) {
    return PRIORITY_LABELS[contact.buying_committee_role];
  }
  return null;
}

export default function CycleCreator({
  accounts, contacts, config, contactPriorities, orgId, leaderId,
  onCreated, onCancel,
  existingCycleId, existingObjectives, existingObjectiveContacts,
}: Props) {
  const { apiPost, apiPut } = useApi();
  const isEditing = !!existingCycleId;

  const [selected, setSelected] = useState<Map<string, SelectedAccount>>(() => {
    if (!existingObjectives) return new Map();
    const map = new Map<string, SelectedAccount>();
    for (const obj of existingObjectives) {
      const account = accounts.find(a => a.id === obj.account_id);
      if (!account) continue;
      const contactIds = new Set(
        (existingObjectiveContacts || [])
          .filter(oc => oc.objective_id === obj.id)
          .map(oc => oc.contact_id)
      );
      map.set(account.id, { account, selectedContactIds: contactIds });
    }
    return map;
  });

  const [accountSearch, setAccountSearch] = useState('');
  const [contactSearches, setContactSearches] = useState<Record<string, string>>({});
  const [expandedAccount, setExpandedAccount] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const priorityMap = useMemo(
    () => new Map(Object.entries(contactPriorities ?? {})),
    [contactPriorities],
  );

  const contactsByAccount = useMemo(() => {
    const map = new Map<string, Contact[]>();
    for (const c of contacts) {
      const list = map.get(c.account_id) || [];
      list.push(c);
      map.set(c.account_id, list);
    }
    for (const [key, list] of map) {
      map.set(key, sortContacts(list, priorityMap));
    }
    return map;
  }, [contacts, priorityMap]);

  const sortedAccounts = useMemo(() => {
    let filtered = accounts;
    if (accountSearch) {
      const q = accountSearch.toLowerCase();
      filtered = accounts.filter(a => a.company_name.toLowerCase().includes(q));
    }
    return [...filtered].sort((a, b) => {
      const aOrder = TIER_ORDER[a.tier] ?? 99;
      const bOrder = TIER_ORDER[b.tier] ?? 99;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (b.tier_score || 0) - (a.tier_score || 0);
    });
  }, [accounts, accountSearch]);

  function setContactSearch(accountId: string, value: string) {
    setContactSearches(prev => ({ ...prev, [accountId]: value }));
  }

  function getFilteredContacts(accountId: string): Contact[] {
    const all = contactsByAccount.get(accountId) || [];
    const search = contactSearches[accountId];
    if (!search) return all;
    const q = search.toLowerCase();
    return all.filter(c =>
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
      c.role?.toLowerCase().includes(q) ||
      c.department?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q)
    );
  }

  function toggleAccount(accountId: string) {
    const next = new Map(selected);
    if (next.has(accountId)) {
      next.delete(accountId);
    } else {
      const account = accounts.find(a => a.id === accountId);
      if (account) next.set(accountId, { account, selectedContactIds: new Set() });
    }
    setSelected(next);
  }

  function toggleContact(accountId: string, contactId: string) {
    const next = new Map(selected);
    let entry = next.get(accountId);

    if (!entry) {
      const account = accounts.find(a => a.id === accountId);
      if (!account) return;
      entry = { account, selectedContactIds: new Set() };
    }

    const ids = new Set(entry.selectedContactIds);
    if (ids.has(contactId)) {
      ids.delete(contactId);
    } else {
      ids.add(contactId);
    }

    if (ids.size === 0 && !selected.has(accountId)) {
      return;
    }

    next.set(accountId, { ...entry, selectedContactIds: ids });
    setSelected(next);
  }

  async function handleSubmit() {
    if (selected.size === 0 || !leaderId) return;
    setSaving(true);

    // The desired objective set (account → selected contacts), in selection
    // order. The AWS endpoints own all cycle/objective/objective-contact writes:
    // POST creates a fresh submitted cycle for the current week, PUT reconciles
    // an existing cycle's objectives server-side (preserving status/notes on the
    // rows that remain). Map iteration order is the new sort_order.
    const objectives = [...selected.entries()].map(([accountId, entry]) => ({
      account_id: accountId,
      contact_ids: [...entry.selectedContactIds],
    }));

    try {
      if (isEditing && existingCycleId) {
        await apiPut(`/v1/mission-control/cycles/${existingCycleId}`, { objectives });
      } else {
        await apiPost('/v1/mission-control/cycles', { org_id: orgId, objectives });
      }
      onCreated();
    } finally {
      setSaving(false);
    }
  }

  const totalContacts = [...selected.values()].reduce((sum, s) => sum + s.selectedContactIds.size, 0);

  return (
    <div className="bg-app-surface rounded-lg border border-app-line overflow-hidden">
      <div className="px-6 py-4 border-b border-app-line flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-app-ink">
            {isEditing ? 'Edit Objectives' : 'Define Objectives'}
          </h3>
          <p className="text-xs text-app-muted mt-0.5">
            Select accounts and contacts to target this cycle
            {config && <span className="text-forest-600"> (suggested: {config.min_accounts} accounts, {config.min_contacts_per_account} contacts each)</span>}
          </p>
        </div>
        <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-app-canvas transition-colors">
          <X className="w-4 h-4 text-app-muted" />
        </button>
      </div>

      <div className="p-4">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-app-muted" />
          <input
            type="text"
            placeholder="Search accounts..."
            value={accountSearch}
            onChange={e => setAccountSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-app-line text-sm focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-500"
          />
        </div>

        <div className="max-h-[480px] overflow-y-auto space-y-1">
          {sortedAccounts.map(account => {
            const isSelected = selected.has(account.id);
            const isExpanded = expandedAccount === account.id;
            const acctContacts = getFilteredContacts(account.id);
            const allContactsCount = (contactsByAccount.get(account.id) || []).length;
            const selectedContacts = selected.get(account.id)?.selectedContactIds || new Set();

            return (
              <div key={account.id} className={`rounded-lg border transition-colors ${isSelected ? 'border-forest-200 bg-forest-50/30' : 'border-app-line'}`}>
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <button
                    onClick={() => toggleAccount(account.id)}
                    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors shrink-0 ${
                      isSelected ? 'bg-forest-600 border-forest-600 text-white' : 'border-app-line-strong hover:border-forest-400'
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3" />}
                  </button>

                  <button
                    onClick={() => setExpandedAccount(isExpanded ? null : account.id)}
                    className="flex items-center gap-2 flex-1 text-left min-w-0"
                  >
                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-app-muted shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-app-muted shrink-0" />}
                    <span className="text-sm font-medium text-app-ink truncate">{account.company_name}</span>

                    {account.tier && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
                        account.tier === 'A' ? 'bg-forest-50 text-forest-700' :
                        account.tier === 'B' ? 'bg-app-warning/10 text-app-warning' :
                        account.tier === 'C' ? 'bg-app-canvas text-app-muted' :
                        'bg-app-canvas text-app-muted'
                      }`}>Tier {account.tier}</span>
                    )}

                    {account.tier_score > 0 && (
                      <span className="text-[10px] text-app-muted shrink-0">{Math.round(account.tier_score)}</span>
                    )}

                    <span className="text-[10px] text-app-muted shrink-0">{allContactsCount} contacts</span>

                    {isSelected && selectedContacts.size > 0 && (
                      <span className="text-[10px] text-forest-600 font-medium shrink-0">{selectedContacts.size} selected</span>
                    )}
                  </button>
                </div>

                {isExpanded && (
                  <div className="px-3 pb-3 pl-11">
                    {allContactsCount > 5 && (
                      <div className="relative mb-2">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-muted" />
                        <input
                          type="text"
                          placeholder="Search contacts..."
                          value={contactSearches[account.id] || ''}
                          onChange={e => setContactSearch(account.id, e.target.value)}
                          className="w-full pl-8 pr-3 py-1.5 rounded-md border border-app-line text-xs focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-500"
                        />
                      </div>
                    )}

                    <div className="space-y-0.5 max-h-[260px] overflow-y-auto">
                      {acctContacts.map(contact => {
                        const cSelected = selectedContacts.has(contact.id);
                        const badge = getContactBadge(contact, priorityMap);
                        const seniorityLabel = SENIORITY_LABELS[contact.seniority] || '';
                        const isHighPriority = getPriorityRank(contact, priorityMap) <= 4;

                        return (
                          <button
                            key={contact.id}
                            onClick={() => toggleContact(account.id, contact.id)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors ${
                              cSelected
                                ? 'bg-forest-50 border border-forest-200'
                                : isHighPriority
                                  ? 'bg-app-warning/10 hover:bg-app-warning/10 border border-app-warning'
                                  : 'hover:bg-app-canvas border border-transparent'
                            }`}
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors shrink-0 ${
                              cSelected ? 'bg-forest-600 border-forest-600 text-white' : 'border-app-line-strong'
                            }`}>
                              {cSelected && <Check className="w-2.5 h-2.5" />}
                            </div>

                            {isHighPriority && !cSelected && (
                              <Star className="w-3 h-3 text-app-warning shrink-0" />
                            )}

                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm text-app-ink truncate">{contact.first_name} {contact.last_name}</p>
                                {badge && (
                                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${badge.bg} ${badge.color}`}>
                                    {badge.label}
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] text-app-muted truncate">{contact.role || contact.department || 'No role'}</p>
                            </div>

                            {seniorityLabel && (
                              <span className={`text-[10px] shrink-0 ${
                                (SENIORITY_RANK[contact.seniority] ?? 99) <= 3 ? 'text-forest-600 font-medium' : 'text-app-muted'
                              }`}>{seniorityLabel}</span>
                            )}
                          </button>
                        );
                      })}

                      {acctContacts.length === 0 && contactSearches[account.id] && (
                        <p className="text-xs text-app-muted py-2 text-center">No contacts match your search</p>
                      )}

                      {allContactsCount === 0 && (
                        <p className="text-xs text-app-muted py-2">No contacts found. Add contacts in the account's Execute tab.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {sortedAccounts.length === 0 && (
            <p className="text-sm text-app-muted text-center py-8">No accounts match your search</p>
          )}
        </div>
      </div>

      <div className="px-6 py-4 border-t border-app-line flex items-center justify-between bg-app-canvas/50">
        <div className="text-xs text-app-muted">
          {selected.size} account{selected.size !== 1 ? 's' : ''}, {totalContacts} contact{totalContacts !== 1 ? 's' : ''} selected
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-app-muted hover:text-app-ink transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || selected.size === 0 || !leaderId}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-forest-600 rounded-lg hover:bg-forest-700 disabled:opacity-50 transition-colors"
          >
            {isEditing ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {saving ? 'Saving...' : isEditing ? 'Update Objectives' : 'Submit Objectives'}
          </button>
        </div>
      </div>
    </div>
  );
}
